import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Wifi, Video, Users, AlertTriangle, Clock, Bell, MessageCircle, Mail, Play } from 'lucide-react';

/**
 * CCTV settings tab — extracted verbatim from SettingsPage.jsx (iter788)
 * to begin decoupling the 2.6k-line settings file. Behaviour is identical;
 * all state + handlers are passed in as props.
 */
export function SettingsCCTVTab({
  cctvSettings, setCctvSettings, saveHikConnect, saveCctvSettings,
  dvrs, newDVR, setNewDVR, addDVR, deleteDVR, branches,
  monitoringConfig, setMonitoringConfig, saveMonitoringConfig, runMonitoringNow,
}) {
  return (
    <div className="space-y-6">
      {/* Hik-Connect Configuration */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Wifi size={18} />
            Hik-Connect Cloud Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Connect your Hikvision account to access cloud-connected DVRs. This is the same account you use in the Hik-Connect mobile app.</p>

          {cctvSettings.hik_status?.connected && (
            <div className="p-3 bg-success/10 rounded-xl border border-success/30 flex items-center gap-2">
              <Wifi size={16} className="text-success" />
              <span className="text-sm font-medium text-success">Connected</span>
              <span className="text-xs text-muted-foreground ml-2">{cctvSettings.hik_status.email}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Hik-Connect Email</Label>
              <Input
                value={cctvSettings.hik_email}
                onChange={(e) => setCctvSettings({ ...cctvSettings, hik_email: e.target.value })}
                placeholder="your@email.com"
                data-testid="hik-email"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={cctvSettings.hik_password}
                onChange={(e) => setCctvSettings({ ...cctvSettings, hik_password: e.target.value })}
                placeholder="••••••••"
                data-testid="hik-password"
              />
            </div>
          </div>
          <Button onClick={saveHikConnect} className="rounded-full" data-testid="save-hik">
            <Wifi size={14} className="mr-2" />Save Hik-Connect Credentials
          </Button>
        </CardContent>
      </Card>

      {/* DVR Management */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Video size={18} />
            DVR/NVR Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Add your DVRs here. For local network DVRs, provide IP address. For cloud DVRs, provide device serial number.</p>

          {dvrs.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Configured DVRs ({dvrs.length})</Label>
              {dvrs.map(dvr => (
                <div key={dvr.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Video size={18} className="text-primary" />
                    <div>
                      <p className="text-sm font-medium">{dvr.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {dvr.branch_name} • {dvr.camera_count || dvr.channels} cameras • {dvr.is_cloud ? 'Cloud' : dvr.ip_address}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-error" onClick={() => deleteDVR(dvr.id)}>Delete</Button>
                </div>
              ))}
            </div>
          )}

          {/* Add New DVR */}
          <div className="p-4 border rounded-xl bg-muted/50 space-y-4">
            <h3 className="font-medium text-sm">Add New DVR</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Branch *</Label>
                <Select value={newDVR.branch_id} onValueChange={(v) => setNewDVR({ ...newDVR, branch_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select Branch" /></SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>DVR Name *</Label>
                <Input value={newDVR.name} onChange={(e) => setNewDVR({ ...newDVR, name: e.target.value })} placeholder="Main Store DVR" />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant={newDVR.is_cloud ? "default" : "outline"}
                size="sm"
                className="flex-1 rounded-xl"
                onClick={() => setNewDVR({ ...newDVR, is_cloud: true })}
              >
                <Wifi size={14} className="mr-1" /> Cloud (Hik-Connect)
              </Button>
              <Button
                variant={!newDVR.is_cloud ? "default" : "outline"}
                size="sm"
                className="flex-1 rounded-xl"
                onClick={() => setNewDVR({ ...newDVR, is_cloud: false })}
              >
                <Video size={14} className="mr-1" /> Local IP
              </Button>
            </div>

            {newDVR.is_cloud ? (
              <div>
                <Label>Device Serial Number</Label>
                <Input value={newDVR.device_serial} onChange={(e) => setNewDVR({ ...newDVR, device_serial: e.target.value })} placeholder="DS-7208HQHI-K1 (found on DVR label)" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>IP Address *</Label>
                  <Input value={newDVR.ip_address} onChange={(e) => setNewDVR({ ...newDVR, ip_address: e.target.value })} placeholder="192.168.1.100" />
                </div>
                <div>
                  <Label>Port</Label>
                  <Input type="number" value={newDVR.port} onChange={(e) => setNewDVR({ ...newDVR, port: parseInt(e.target.value) })} />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input value={newDVR.username} onChange={(e) => setNewDVR({ ...newDVR, username: e.target.value })} />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input type="password" value={newDVR.password} onChange={(e) => setNewDVR({ ...newDVR, password: e.target.value })} />
                </div>
              </div>
            )}

            <div>
              <Label>Number of Channels</Label>
              <Select value={String(newDVR.channels)} onValueChange={(v) => setNewDVR({ ...newDVR, channels: parseInt(v) })}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[4, 8, 16, 32].map(n => <SelectItem key={n} value={String(n)}>{n} Channels</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={addDVR} disabled={!newDVR.branch_id || !newDVR.name} className="rounded-full">
              Add DVR
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AI Features */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Users size={18} />
            AI Features Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Configure AI-powered features for your CCTV system.</p>

          <div className="space-y-4">
            {/* People Counting */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-success/10 rounded-lg"><Users size={18} className="text-success" /></div>
                <div>
                  <p className="text-sm font-medium">People Counting</p>
                  <p className="text-xs text-muted-foreground">AI counts people entering and exiting your stores</p>
                </div>
              </div>
              <Switch
                checked={cctvSettings.people_counting_enabled}
                onCheckedChange={(v) => setCctvSettings({ ...cctvSettings, people_counting_enabled: v })}
              />
            </div>

            {cctvSettings.people_counting_enabled && (
              <div className="ml-6 p-3 border-l-2 border-primary/30 space-y-3">
                <div>
                  <Label className="text-xs">Counting Interval (minutes)</Label>
                  <Select value={String(cctvSettings.counting_interval)} onValueChange={(v) => setCctvSettings({ ...cctvSettings, counting_interval: parseInt(v) })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 min</SelectItem>
                      <SelectItem value="5">5 min</SelectItem>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Motion Alerts */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-warning/10 rounded-lg"><AlertTriangle size={18} className="text-warning" /></div>
                <div>
                  <p className="text-sm font-medium">Motion Detection Alerts</p>
                  <p className="text-xs text-muted-foreground">Get alerts when motion is detected after hours</p>
                </div>
              </div>
              <Switch
                checked={cctvSettings.motion_alerts_enabled}
                onCheckedChange={(v) => setCctvSettings({ ...cctvSettings, motion_alerts_enabled: v })}
              />
            </div>

            {cctvSettings.motion_alerts_enabled && (
              <div className="ml-6 p-3 border-l-2 border-warning/30 space-y-3">
                <div>
                  <Label className="text-xs">Alert Sensitivity</Label>
                  <Select value={cctvSettings.alert_sensitivity} onValueChange={(v) => setCctvSettings({ ...cctvSettings, alert_sensitivity: v })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <Button onClick={saveCctvSettings} className="rounded-full">
            Save AI Settings
          </Button>
        </CardContent>
      </Card>

      {/* Scheduled AI Monitoring */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Clock size={18} />
            Scheduled AI Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Configure automatic AI monitoring that runs at regular intervals. Requires camera frames to be uploaded or direct camera access.</p>

          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg"><Clock size={18} className="text-primary" /></div>
              <div>
                <p className="text-sm font-medium">Enable Scheduled Monitoring</p>
                <p className="text-xs text-muted-foreground">Automatically analyze camera feeds at set intervals</p>
              </div>
            </div>
            <Switch
              checked={monitoringConfig.enabled}
              onCheckedChange={(v) => setMonitoringConfig({ ...monitoringConfig, enabled: v })}
              data-testid="monitoring-enabled"
            />
          </div>

          {monitoringConfig.enabled && (
            <div className="space-y-4 p-4 border rounded-xl bg-white">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Monitoring Interval</Label>
                  <Select value={String(monitoringConfig.interval_minutes)} onValueChange={(v) => setMonitoringConfig({ ...monitoringConfig, interval_minutes: parseInt(v) })}>
                    <SelectTrigger data-testid="monitoring-interval">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Every 1 minute</SelectItem>
                      <SelectItem value="5">Every 5 minutes</SelectItem>
                      <SelectItem value="15">Every 15 minutes</SelectItem>
                      <SelectItem value="30">Every 30 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs mb-2 block">Features to Enable</Label>
                <div className="flex flex-wrap gap-2">
                  {['people_counting', 'motion_detection', 'object_detection'].map(feature => (
                    <button
                      key={feature}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        monitoringConfig.features.includes(feature)
                          ? 'bg-primary text-white'
                          : 'bg-muted text-muted-foreground hover:bg-muted'
                      }`}
                      onClick={() => {
                        const features = monitoringConfig.features.includes(feature)
                          ? monitoringConfig.features.filter(f => f !== feature)
                          : [...monitoringConfig.features, feature];
                        setMonitoringConfig({ ...monitoringConfig, features });
                      }}
                    >
                      {feature.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs mb-2 block">Alert Notification Channels</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'in_app', label: 'In-App', icon: Bell },
                    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
                    { id: 'email', label: 'Email', icon: Mail }
                  ].map(channel => (
                    <button
                      key={channel.id}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${
                        monitoringConfig.notification_channels.includes(channel.id)
                          ? 'bg-primary text-white'
                          : 'bg-muted text-muted-foreground hover:bg-muted'
                      }`}
                      onClick={() => {
                        const channels = monitoringConfig.notification_channels.includes(channel.id)
                          ? monitoringConfig.notification_channels.filter(c => c !== channel.id)
                          : [...monitoringConfig.notification_channels, channel.id];
                        setMonitoringConfig({ ...monitoringConfig, notification_channels: channels });
                      }}
                    >
                      <channel.icon size={12} />
                      {channel.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={saveMonitoringConfig} className="rounded-full">
                  Save Monitoring Config
                </Button>
                <Button variant="outline" onClick={runMonitoringNow} className="rounded-full">
                  <Play size={14} className="mr-1" /> Run Now
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Card */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="pt-4">
          <h3 className="font-medium text-sm mb-2">How to Find Your DVR Serial Number</h3>
          <ol className="text-xs text-muted-foreground space-y-1 ml-4 list-decimal">
            <li>Look at the sticker on your DVR/NVR device</li>
            <li>Or open the Hik-Connect mobile app → Device Management → Select your device → Device Info</li>
            <li>The serial number looks like: DS-7208HQHI-K1</li>
          </ol>
          <p className="text-xs text-muted-foreground mt-3">
            <strong>Note:</strong> For live streaming via cloud, full API access requires Hikvision Partner registration at <a href="https://tpp.hikvision.com" target="_blank" rel="noreferrer" className="text-primary underline">tpp.hikvision.com</a>.
            For immediate viewing, use local IP connection or the Hik-Connect mobile app.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default SettingsCCTVTab;
